import type { CatalogItem } from '../types';

const stripAccents = (str: string) => (str || '')
  .toLowerCase()
  .replace(/i̇/g, 'i').replace(/ı/g, 'i')
  .replace(/ş/g, 's').replace(/ç/g, 'c')
  .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ö/g, 'o')
  .replace(/[^a-z0-9]/g, '');

const CODE_HEADERS = ['kod', 'code', 'urunkodu', 'productcode', 'malzemekodu', 'stokkodu', 'artno', 'sku'];

// Katalog kodları kataloğun kendi eşleştirmesiyle aynı şekilde karşılaştırılır
// (trim + büyük harf — bkz. AppContext `safeDocId` / Excel içe aktarma).
export const catalogCodeKey = (code: string) => String(code ?? '').trim().toUpperCase();

/**
 * Excel satırlarından (header:1 biçimi) ürün kodlarını okur.
 * Kod sütunu başlığı bulunamazsa `null` döner — tahmin yürütmez, çünkü sonuç
 * silme kararında kullanılır.
 */
export function extractCodesFromRows(rows: any[][]): string[] | null {
  const scan = Math.min(rows.length, 15);
  for (let r = 0; r < scan; r++) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;
    const codeIdx = row.findIndex(cell => CODE_HEADERS.includes(stripAccents(String(cell ?? ''))));
    if (codeIdx === -1) continue;
    const codes: string[] = [];
    for (const dataRow of rows.slice(r + 1)) {
      if (!Array.isArray(dataRow)) continue;
      const key = catalogCodeKey(dataRow[codeIdx]);
      if (key) codes.push(key);
    }
    return codes;
  }
  return null;
}

export interface CatalogPruneReview {
  /** Katalogda olup dosyada olmayan ürünler. */
  missing: CatalogItem[];
  /** Dosyadaki benzersiz kod sayısı. */
  fileCodeCount: number;
  /** Dosyadaki kodlardan katalogda karşılığı olan sayı. */
  matchedCount: number;
}

export function findCatalogItemsMissingFromCodes(catalog: CatalogItem[], fileCodes: string[]): CatalogPruneReview {
  const fileSet = new Set(fileCodes.map(catalogCodeKey).filter(Boolean));
  const catalogSet = new Set(catalog.map(i => catalogCodeKey(i.code)));
  const missing = catalog.filter(i => !fileSet.has(catalogCodeKey(i.code)));
  let matchedCount = 0;
  fileSet.forEach(k => { if (catalogSet.has(k)) matchedCount++; });
  return { missing, fileCodeCount: fileSet.size, matchedCount };
}

/** Yanlış dosya seçildiğinde (kodların çoğu katalogla eşleşmiyorsa) silme listesi güvenilmez. */
export const MIN_MATCH_RATIO = 0.5;

export const isReviewTrustworthy = (review: CatalogPruneReview) =>
  review.fileCodeCount > 0 && review.matchedCount / review.fileCodeCount >= MIN_MATCH_RATIO;

export interface ProposalUsageRef {
  id: string;
  clientName: string;
  status?: string;
}

/**
 * Tekliflerin içinde (tekil teklifte `specItems`, toplu projede `units[].specItems`)
 * verilen kodlardan birini taşıyan kayıtları bulur. Dokümanı özyinelemeli gezer ve
 * string `code` alanı olan her düğümü kontrol eder; yapı değişse de yakalar.
 * Anahtar: katalog kodu (trim + büyük harf), değer: o kodu kullanan teklifler.
 */
export function findCodeUsageInProposals(
  proposals: Array<Record<string, any>>,
  codes: string[],
): Map<string, ProposalUsageRef[]> {
  const wanted = new Set(codes.map(catalogCodeKey));
  const usage = new Map<string, ProposalUsageRef[]>();

  const walk = (node: any, found: Set<string>) => {
    if (Array.isArray(node)) { node.forEach(n => walk(n, found)); return; }
    if (!node || typeof node !== 'object') return;
    if (typeof node.code === 'string') {
      const key = catalogCodeKey(node.code);
      if (wanted.has(key)) found.add(key);
    }
    Object.values(node).forEach(v => { if (v && typeof v === 'object') walk(v, found); });
  };

  for (const p of proposals) {
    const found = new Set<string>();
    walk(p, found);
    found.forEach(key => {
      const list = usage.get(key) ?? [];
      list.push({ id: String(p.id ?? ''), clientName: String(p.clientName ?? p.client?.clientName ?? ''), status: p.status });
      usage.set(key, list);
    });
  }
  return usage;
}
